/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 24-09-2026
 *
 * De registratie van de twee cytoscape-canvassen, het modelcanvas en het simcanvas. Een canvas meldt
 * zich hier aan zodra zijn cytoscape-instantie er is (`DlCytoCanvasBaseDirective`) en af bij het
 * opruimen; wie de ruwe `Core` nodig heeft, vraagt hem hier op of wacht erop.
 *
 * Tot golf 4f zat dit in de migratie-interface, die het voor de AngularJS-controllers en voor de
 * browsertests bijhield. Het verhuisde 1-op-1 (plan-golf-4f.md, B6); samenvoegen met de
 * `GlobalModelcanvasService` en de `GlobalSimcanvasService` is opruimwerk voor later.
 */
import {inject, Injectable} from '@angular/core';
import type {Core} from 'cytoscape';
import {SamenwerkprotocolService} from '../samenwerking/samenwerkprotocol.service';
import type {Simulatie_eiland} from '../samenwerking/protocol.types';

/**
 * Aanduiding van een van de twee cytoscape-canvassen.
 */
export type CytoCanvasView = 'model' | 'sim';

/**
 * Wat een canvas bij de registratie aanbiedt: zijn cytoscape-instantie, of `null` zolang die er niet is.
 */
export interface CytoCanvasEiland {
    readonly cy: Core | null;
}

@Injectable({providedIn: 'root'})
export class CanvasregistratieService {

    /**
     * De samenwerk-baas: zijn simulatiebaan werkt op het simcanvas, en hoort dus wanneer dat er is
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /**
     * De aangemelde canvassen per view
     */
    private readonly canvassen = new Map<CytoCanvasView, CytoCanvasEiland>();

    /**
     * Wie per view op een canvas wacht; ze krijgen hun antwoord zodra dat canvas zich aanmeldt
     */
    private readonly wachters = new Map<CytoCanvasView, Array<(cy: Core) => void>>();

    /**
     * Meldt een canvas aan onder zijn view, en beantwoordt de `whenCanvas`-vragen die erop wachtten. Het
     * simcanvas gaat ook door naar de samenwerk-baas, in plaats van via een eigen aanmeldkanaal
     * (werkregel 21-08-2026).
     * @param view
     * @param eiland
     */
    registreerCanvas(view: CytoCanvasView, eiland: CytoCanvasEiland): void {
        this.canvassen.set(view, eiland);
        if (view === 'sim') {
            this.samenwerkprotocol.registreerSimcanvas(eiland as unknown as Simulatie_eiland);
        }
        const wachtend = this.wachters.get(view);
        if (wachtend && eiland.cy) {
            this.wachters.delete(view);
            for (const antwoord of wachtend) {
                antwoord(eiland.cy);
            }
        }
    }

    /**
     * Meldt een canvas af, maar alleen als het nog het aangemelde canvas van die view is
     * @param view
     * @param eiland
     */
    deregistreerCanvas(view: CytoCanvasView, eiland: CytoCanvasEiland): void {
        if (this.canvassen.get(view) !== eiland) {
            return;
        }
        this.canvassen.delete(view);
        if (view === 'sim') {
            this.samenwerkprotocol.registreerSimcanvas(null);
        }
    }

    /**
     * De cytoscape-instantie van een view, of `null` als dat canvas er (nog) niet is
     * @param view
     */
    getCanvas(view: CytoCanvasView): Core | null {
        return this.canvassen.get(view)?.cy ?? null;
    }

    /**
     * De cytoscape-instantie van een view zodra die er is; meteen als het canvas al aangemeld is
     * @param view
     */
    whenCanvas(view: CytoCanvasView): Promise<Core> {
        const huidig = this.getCanvas(view);
        if (huidig) {
            return Promise.resolve(huidig);
        }
        return new Promise<Core>(antwoord => {
            this.wachters.set(view, [...(this.wachters.get(view) ?? []), antwoord]);
        });
    }
}
